import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ComparisonDetailPageDto,
  TableDataDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  DetailPageCreationModalProps,
  isNotNullOrUndefined,
} from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';

import {
  useGlobalSelections,
  useSelections,
} from '../../store/hooks/useSelections';
import { buildComparisonDetailPageUrl } from '../../utils/comparisonDetailPageEntities';
import { DetailPageNavigationState } from '../../utils/detailPageLinkDisplay';

import { getDetailPageUrl } from './getDetailPageUrl';
import { useOriginHrefForDetailsPage } from './useOriginHrefForDetailsPage';

export type UseComparisonDetailPageModalPropertiesParams = {
  comparisonDetailPages: ComparisonDetailPageDto[];
  pageFilterInstanceId: string;
  roleId: string;
};

/**
 * Props returned by the hook — everything DetailPageCreationModal needs
 * except `listIcon` and `size`, which are static and added at the call site.
 */
export type ComparisonDetailPageModalCoreProps = Omit<
  DetailPageCreationModalProps,
  'listIcon' | 'size'
>;

export type UseComparisonDetailPageModalPropertiesReturnValue = {
  comparisonDetailPageModalProps?: ComparisonDetailPageModalCoreProps;
  open: (selectedRows: TableDataDto['items']) => void;
};

function buildComparisonModalItems(
  selectedRows: TableDataDto['items'],
  identifierColumnKey: string,
  identifierColumnLabel?: string
) {
  const seen = new Set<string>();
  return selectedRows
    .map((row) => {
      const id = row[identifierColumnKey];
      if (!isNotNullOrUndefined(id)) {
        return undefined;
      }

      const idString = String(id);
      if (seen.has(idString)) {
        return undefined;
      }
      seen.add(idString);

      const label = identifierColumnLabel
        ? String(row[identifierColumnLabel] ?? idString)
        : idString;

      return { id: idString, label };
    })
    .filter(isNotNullOrUndefined);
}

export function useComparisonDetailPageModalProperties({
  comparisonDetailPages,
  pageFilterInstanceId,
  roleId,
}: UseComparisonDetailPageModalPropertiesParams): UseComparisonDetailPageModalPropertiesReturnValue {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, { open: openModal, close }] = useDisclosure();
  const [selectedRows, setSelectedRows] = useState<TableDataDto['items']>([]);
  const [currentLayout, setCurrentLayout] = useState<string | null>(null);

  const originHref = useOriginHrefForDetailsPage();
  const globalFilterSelections = useGlobalSelections();
  const pageFilterSelections = useSelections(pageFilterInstanceId);
  const pageFilter = useMemo(
    () => ({ ...globalFilterSelections, ...pageFilterSelections }),
    [globalFilterSelections, pageFilterSelections]
  );

  const comparisonDetailPageModalProps = useMemo<
    ComparisonDetailPageModalCoreProps | undefined
  >(() => {
    if (!isOpen || comparisonDetailPages.length === 0) {
      return undefined;
    }

    // Resolve the page for the currently selected layout, falling back to the
    // first page. This must stay in sync with the modal's layout dropdown so
    // that items are always built from the correct identifierColumnKey.
    const activePage =
      comparisonDetailPages.find((p) => p.id === currentLayout) ??
      comparisonDetailPages[0];

    const items = buildComparisonModalItems(
      selectedRows,
      activePage.identifierColumnKey,
      activePage.identifierColumnLabel
    );

    const warningText =
      items.length < selectedRows.length
        ? t('app.detailPageCreationModal.filteredRowsWarning')
        : undefined;

    return {
      applyDisabled: items.length < 2,
      initialItems: items,
      initialLayout: activePage.id,
      layouts: comparisonDetailPages.map((page) => ({
        value: page.id,
        label: page.title,
      })),
      modalOpened: isOpen,
      onCancel: close,
      onLayoutChange: setCurrentLayout,
      warningText,
      onCommit: ({ layout }) => {
        const selectedPage =
          comparisonDetailPages.find((page) => page.id === layout) ??
          comparisonDetailPages[0];

        // Re-derive from selectedRows rather than the modal's local `items`
        // state. The modal updates items asynchronously via useEffect when the
        // layout changes, so `committedItems` can be stale if Apply is clicked
        // immediately after switching layouts.
        const freshItems = buildComparisonModalItems(
          selectedRows,
          selectedPage.identifierColumnKey,
          selectedPage.identifierColumnLabel
        );
        const entityIds = freshItems.map((item) => item.id);

        if (entityIds.length < 2) {
          return;
        }

        const baseHref = getDetailPageUrl(
          roleId,
          {
            ...selectedPage,
            detailsIdKey: selectedPage.identifierColumnKey,
            additionalFilterColumnKeys: [],
          },
          pageFilter,
          []
        );

        const href = buildComparisonDetailPageUrl(
          baseHref,
          selectedPage.identifierColumnKey,
          entityIds
        );

        const detailsDiscriminatorDisplayValue =
          selectedPage.identifierColumnLabel != null
            ? `${freshItems[0]?.label} vs ${freshItems[1]?.label}`
            : undefined;

        const state: DetailPageNavigationState = {
          originHref,
          ...(detailsDiscriminatorDisplayValue != null && {
            detailsDiscriminatorDisplayValue,
          }),
        };

        navigate(href, { state });
        close();
      },
    };
  }, [
    close,
    comparisonDetailPages,
    currentLayout,
    isOpen,
    navigate,
    originHref,
    pageFilter,
    roleId,
    selectedRows,
    t,
  ]);

  return {
    open: (rows: TableDataDto['items']) => {
      setSelectedRows(rows);
      setCurrentLayout(null);
      openModal();
    },
    comparisonDetailPageModalProps,
  };
}
